import { useState } from "react";
import Layout from "../../layout/Layout";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AssignmentIcon from "@mui/icons-material/Assignment";
import QuizIcon from "@mui/icons-material/Quiz";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import LoginIcon from "@mui/icons-material/Login";

const Documentation = () => {
  const { t } = useTranslation("documentation");
  const [videoTab, setVideoTab] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const handleVideoTabChange = (event, newValue) => {
    setVideoTab(newValue);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const quickStartCards = [
    {
      icon: <LoginIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      titleKey: "quickStart.gettingStarted.title",
      descriptionKey: "quickStart.gettingStarted.description",
    },
    {
      icon: <HomeIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      titleKey: "quickStart.dashboard.title",
      descriptionKey: "quickStart.dashboard.description",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      titleKey: "quickStart.citizens.title",
      descriptionKey: "quickStart.citizens.description",
    },
    {
      icon: <FavoriteIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      titleKey: "quickStart.metrics.title",
      descriptionKey: "quickStart.metrics.description",
    },
    {
      icon: <AssignmentIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      titleKey: "quickStart.recommendations.title",
      descriptionKey: "quickStart.recommendations.description",
    },
    {
      icon: <QuizIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      titleKey: "quickStart.questionnaires.title",
      descriptionKey: "quickStart.questionnaires.description",
    },
  ];

  const accordionSections = [
    {
      id: "getting-started",
      titleKey: "sections.gettingStarted.title",
      contentKey: "sections.gettingStarted.content",
    },
    {
      id: "dashboard",
      titleKey: "sections.dashboard.title",
      contentKey: "sections.dashboard.content",
    },
    {
      id: "citizens",
      titleKey: "sections.citizens.title",
      contentKey: "sections.citizens.content",
    },
    {
      id: "metrics",
      titleKey: "sections.metrics.title",
      contentKey: "sections.metrics.content",
    },
    {
      id: "recommendations",
      titleKey: "sections.recommendations.title",
      contentKey: "sections.recommendations.content",
    },
    {
      id: "questionnaires",
      titleKey: "sections.questionnaires.title",
      contentKey: "sections.questionnaires.content",
    },
  ];

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            {t("title")}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 4, fontSize: { xs: "0.9rem", sm: "1.1rem" } }}
          >
            {t("subtitle")}
          </Typography>
        </Box>

        {/* Video Tutorials Section */}
        <Paper elevation={2} sx={{ mb: 6, p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PlayCircleIcon sx={{ fontSize: 32, color: "primary.main", mr: 1 }} />
            <Typography variant="h5" component="h2" fontWeight={500}>
              {t("videoSection.title")}
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={videoTab}
              onChange={handleVideoTabChange}
              aria-label="video language tabs"
            >
              <Tab label={t("videoSection.englishTab")} />
              <Tab label={t("videoSection.romanianTab")} />
            </Tabs>
          </Box>

          <Box
            sx={{
              position: "relative",
              paddingBottom: "56.25%",
              height: 0,
              overflow: "hidden",
              borderRadius: 1,
            }}
          >
            {videoTab === 0 && (
              <iframe
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
                src="https://www.youtube.com/embed/M3m1kKUSx2Q"
                title="DEPO Tutorial - English"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
            {videoTab === 1 && (
              <iframe
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
                src="https://www.youtube.com/embed/olIymMOBjQs"
                title="DEPO Tutorial - Romanian"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </Box>
        </Paper>

        {/* Quick Start Cards */}
        <Typography variant="h5" component="h2" fontWeight={500} sx={{ mb: 3 }}>
          {t("quickStart.title")}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 6 }}>
          {quickStartCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: "center", p: 3 }}>
                  <Box sx={{ mb: 2 }}>{card.icon}</Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    fontWeight={500}
                  >
                    {t(card.titleKey)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(card.descriptionKey)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Detailed Documentation Accordion */}
        <Typography variant="h5" component="h2" fontWeight={500} sx={{ mb: 3 }}>
          {t("detailedGuide.title")}
        </Typography>

        <Box sx={{ mb: 4 }}>
          {accordionSections.map((section) => (
            <Accordion
              key={section.id}
              expanded={expandedAccordion === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${section.id}-content`}
                id={`${section.id}-header`}
              >
                <Typography fontWeight={500}>{t(section.titleKey)}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: "pre-line",
                    "& ul": { pl: 2, mt: 1 },
                    "& li": { mb: 0.5 },
                  }}
                >
                  {t(section.contentKey)}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Layout>
  );
};

export default Documentation;
